/**
 * Generate Google Calendar URL for booking
 * @param {Object} booking - Booking object
 * @returns {string} Google Calendar URL
 */
export const generateGoogleCalendarUrl = (booking) => {
  const baseUrl = 'https://calendar.google.com/calendar/render';
  
  // Parse date and time
  const dateStr = booking.scheduled_date; // "2026-08-15"
  const timeStr = booking.scheduled_time; // "10:00 AM" or "14:00"
  
  // Convert time to 24h format if needed
  let hour = 0;
  let minute = 0;
  
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    const timeParts = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (timeParts) {
      hour = parseInt(timeParts[1]);
      minute = parseInt(timeParts[2]);
      const isPM = timeParts[3].toUpperCase() === 'PM';
      if (isPM && hour !== 12) hour += 12;
      if (!isPM && hour === 12) hour = 0;
    }
  } else {
    const timeParts = timeStr.split(':');
    hour = parseInt(timeParts[0]);
    minute = parseInt(timeParts[1] || '0');
  }
  
  // Create start datetime
  const startDate = new Date(`${dateStr}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`);
  
  // Calculate end time based on duration
  const durationHours = booking.duration_hours || 2;
  const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);
  
  // Format dates for Google Calendar (YYYYMMDDTHHmmss)
  const formatDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };
  
  const startFormatted = formatDate(startDate);
  const endFormatted = formatDate(endDate);
  
  // Build address string
  const address = [
    booking.address,
    booking.city,
    booking.state,
    booking.zip_code
  ].filter(Boolean).join(', ');
  
  // Build description
  const description = [
    `Service: ${booking.service_name}`,
    `Duration: ${durationHours} hours`,
    booking.worker_name ? `Worker: ${booking.worker_name}` : '',
    booking.special_instructions ? `Notes: ${booking.special_instructions}` : '',
    '',
    'Booked via NC Help Hub',
    'https://nchelphub.com'
  ].filter(Boolean).join('\n');
  
  // Build URL params
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `NC Help Hub - ${booking.service_name}`,
    dates: `${startFormatted}/${endFormatted}`,
    details: description,
    location: address,
    sf: 'true'
  });
  
  return `${baseUrl}?${params.toString()}`;
};

/**
 * Generate ICS file content for calendar download
 * @param {Object} booking - Booking object
 * @returns {string} ICS file content
 */
export const generateICSContent = (booking) => {
  // Parse date and time
  const dateStr = booking.scheduled_date;
  const timeStr = booking.scheduled_time;
  
  let hour = 0;
  let minute = 0;
  
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    const timeParts = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (timeParts) {
      hour = parseInt(timeParts[1]);
      minute = parseInt(timeParts[2]);
      const isPM = timeParts[3].toUpperCase() === 'PM';
      if (isPM && hour !== 12) hour += 12;
      if (!isPM && hour === 12) hour = 0;
    }
  } else {
    const timeParts = timeStr.split(':');
    hour = parseInt(timeParts[0]);
    minute = parseInt(timeParts[1] || '0');
  }
  
  const startDate = new Date(`${dateStr}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`);
  const durationHours = booking.duration_hours || 2;
  const endDate = new Date(startDate.getTime() + durationHours * 60 * 60 * 1000);
  
  const formatICSDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '').slice(0, -1);
  };
  
  const address = [booking.address, booking.city, booking.state, booking.zip_code].filter(Boolean).join(', ');
  
  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NC Help Hub//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${booking.id}@nchelphub.com`,
    `DTSTAMP:${formatICSDate(new Date())}Z`,
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:NC Help Hub - ${booking.service_name}`,
    `DESCRIPTION:Service: ${booking.service_name}\\nDuration: ${durationHours} hours${booking.worker_name ? '\\nWorker: ' + booking.worker_name : ''}`,
    `LOCATION:${address}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');
  
  return icsContent;
};

/**
 * Download ICS file
 * @param {Object} booking - Booking object
 */
export const downloadICSFile = (booking) => {
  const icsContent = generateICSContent(booking);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `nc-help-hub-${booking.service_name.toLowerCase().replace(/\s+/g, '-')}-${booking.scheduled_date}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Get week dates for a given date
 * @param {Date} date - Reference date
 * @returns {Array} Array of dates for the week
 */
export const getWeekDates = (date) => {
  const week = [];
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay()); // Start from Sunday
  
  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    week.push(day);
  }
  
  return week;
};

/**
 * Format time slot for display
 * @param {string} time - Time string
 * @returns {string} Formatted time
 */
export const formatTimeSlot = (time) => {
  if (!time) return '';
  
  // If already in 12h format, return as is
  if (time.includes('AM') || time.includes('PM')) {
    return time;
  }
  
  // Convert 24h to 12h
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  
  return `${hour12}:${minutes || '00'} ${ampm}`;
};
